import chunk from 'lodash.chunk'

import { PlaybackTelemetry } from '..'
import { StoreRecord, Chapter } from '../types'
import { TELEMETRY_INDEXES } from '../utils'

import logger from '../../../utils/logger'

import { VIDEO_TYPE } from './enums'

import { StorageSchema } from '../../../utils/storage'

import {
  ChapterData,
  ChapterProgressData,
  VideoChapterEndedTelemetryEvent,
  VideoChapterStartedTelemetryEvent,
  VideoEndedTelemetryEvent,
  VideoProgressedTelemetryEvent,
  VideoRangeEndedTelemetryEvent,
  VideoRangeStartedTelemetryEvent,
  VideoStartedTelemetryEvent
} from './events'

import { VideoPlaybackTelemetryAPI } from '../../api/playback/telemetry'
import { HTTPRequestError, MissingTelemetryEventsError } from '../../../errors'
import { EVENT_PLAYBACK_STATUS } from '../../../constants/enums'
import { toFixed } from '../../../utils'

export type VideoEvent = VideoStartedTelemetryEvent |
VideoEndedTelemetryEvent | VideoChapterStartedTelemetryEvent |
VideoChapterEndedTelemetryEvent | VideoRangeStartedTelemetryEvent |
VideoRangeEndedTelemetryEvent | VideoProgressedTelemetryEvent

export type VideoContent = {
  videoType: VIDEO_TYPE
  productId: string
  releaseId: string
}

interface Schema extends StorageSchema {
  video: {
    key: number,
    value: StoreRecord<VideoEvent>
  }
}

const isPending = (reason?: EVENT_PLAYBACK_STATUS) => reason === EVENT_PLAYBACK_STATUS.PENDING
const isSending = (reason?: EVENT_PLAYBACK_STATUS) => reason === EVENT_PLAYBACK_STATUS.SENDING

const byType = (record: StoreRecord<VideoEvent>) => !isPending(record.event.data['reason' as never])
const byRequestStatusSending = (record: StoreRecord<VideoEvent>) => !isSending(record.event.data['requestStatus' as never])

const TELEMETRY_EVENTS_CHUNK_SIZE = 10
const TIMER = 60000

export { isPending, isSending, byType, byRequestStatusSending, TELEMETRY_EVENTS_CHUNK_SIZE }

/**
 * TelemetryEventManager
 *
 * This class is responsible for handling telemetry event updates.
 *
 * Features:
 * 1. Creation of event updates based on the telemetry data received.
 * 2. Persistently stores the telemetry events in an async storage, ensuring that events are not lost even if the application restarts or faces disruptions.
 * 3. Periodically or upon certain triggers, it sends the stored telemetry events to a designated server for further processing and analysis.
 *
 * Usage:
 * - Instantiate an object of this class when you need to manage telemetry events.
 * - Use the provided methods to add, store, and transmit events as required.
 *
 * Note:
 * Ensure that the async storage and server endpoints are properly configured to avoid potential data loss or transmission failures.
 */
export class TelemetryEventManager extends PlaybackTelemetry<VideoContent, Schema, VideoEvent> {
  #api = new VideoPlaybackTelemetryAPI('v2')

  protected get log() {
    return this.logger.VIOLET.OUTLINE
  }

  protected get name() {
    return 'Telemetry'
  }

  public get logger() {
    return logger
  }

  public async save<T>(data: T) {
    if (!data) return logger.WARNING(this.name, '[event:save:error]', 'No data provided. Skip `save`')

    await this.create()
    await this.storage.stores.video.save(data, data['id' as never])
  }

  public get api(): VideoPlaybackTelemetryAPI {
    return this.#api
  }

  public async update(event: VideoEvent) {
    const index = this.replace(event)
    const payload = this.store[index]

    this.log(this.name, `[event:update]`, { id: event.id, data: event.data, reason: event.data.reason, type: event.type })

    await this.save(payload)
  }

  public async sync() {
    await this.storage.stores.video.clear()
    await this.storage.stores.video.save(this.store)
  }

  public async started() {
    this.log(this.name, '[event:start]')

    const [event, payload] = this.push(new VideoStartedTelemetryEvent)

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async progressed(
    position: number,
    maxPosition: number,
    progress: number,
    duration: number,
    status = EVENT_PLAYBACK_STATUS.PLAYING,
    chapter?: Chapter
  ) {
    const [event, payload] = this.push(new VideoProgressedTelemetryEvent(position, maxPosition, progress, duration, status, chapter))

    this.log(this.name, '[event:progress]', event)

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async ended(
    position: number,
    maxPosition: number,
    progress: number,
    duration: number,
    reason = EVENT_PLAYBACK_STATUS.COMPLETED,
    chapter?: Chapter
  ) {
    const [event, payload] = this.push(new VideoEndedTelemetryEvent(position, maxPosition, progress, duration, reason, chapter))

    this.log(this.name, '[event:end]', event)

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async chapterStarted(chapter: Chapter) {
    const [event, payload] = this.push(new VideoChapterStartedTelemetryEvent(chapter))

    this.log(this.name, '[event:chapter:start]', event)

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async chapterEnded(
    position: number,
    maxPosition: number,
    duration: number,
    progress: number,
    chapter: Chapter,
    reason = EVENT_PLAYBACK_STATUS.PENDING) {
    const [event, payload] = this.push(
      new VideoChapterEndedTelemetryEvent(position, maxPosition, duration, progress, chapter, reason)
    )

    this.log(this.name, `[event:chapter:end:${reason}]`, event)

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async rangeStarted(start: number) {
    const [event, payload] = this.push(new VideoRangeStartedTelemetryEvent(start))

    this.log(this.name, '[event:range:start]', start)

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  public async rangeEnded(start: number, end: number, duration: number, reason = EVENT_PLAYBACK_STATUS.PENDING) {
    const [event, payload] = this.push(new VideoRangeEndedTelemetryEvent(start, end, duration, reason))

    this.log(this.name, '[event:range:end]', event)

    if (this.config.autosave) await this.save(payload)

    return event as VideoEvent
  }

  async #fetch(events: StoreRecord<VideoEvent>[], ignoreIndex: boolean) {
    if (!this.session || !this.storage) throw new Error('Missing session and storage')

    const index = {
      ...TELEMETRY_INDEXES[1],
      ...{ options: undefined, value: this.session.id }
    }

    try {
      for (const item of events) {
        item.event.data['requestStatus'] = EVENT_PLAYBACK_STATUS.SENDING
        this.replace(item.event)
      }

      const invalid = await this.api.send(events.map(i => i.event))
      const list = invalid.length ? invalid : events.map(event => event.id)

      this.remove(list)
      await this.storage.stores.video.remove(list, ignoreIndex ? undefined : index)

      return true
    } catch (error) {
      if (error instanceof HTTPRequestError) {
        this.save(events.map(i => {
          i.status = EVENT_PLAYBACK_STATUS.FAILED
          return i
        }))
      } else {
        console.warn('Storage error:', error)
      }
      return false
    }
  }

  public async sendStored(complete = true, reset = false) {
    if (reset) this.reset()

    await this.create()

    const events: Array<StoreRecord<VideoEvent>> = await this.storage.stores.video.all()

    let list = events.map(i => {
      if (isPending(i.event.data['reason' as never]) && complete)
        i.event.data['reason'] = EVENT_PLAYBACK_STATUS.COMPLETED
      return i
    })

    if (!complete) list = list.filter(byType)
    await this.send(list, true)
  }

  public async send(list?: Array<StoreRecord<VideoEvent>>, ignoreIndex = false) {
    const events = chunk(list?.filter(byRequestStatusSending) || this.store.filter(byType).filter(byRequestStatusSending), TELEMETRY_EVENTS_CHUNK_SIZE)

    if (!events.length) throw new MissingTelemetryEventsError
    await Promise.all(events.map(butch => this.#fetch(butch, ignoreIndex)))
  }
}


/**
 * VideoPlaybackTelemetry
 *
 * Extends the base functionality provided by the TelemetryEventManager to offer advanced data preparation and event modification capabilities.
 *
 * Features:
 * 1. Advanced Data Preparation: Before creating event updates, it can preprocess, cleanse, or transform telemetry data to ensure it's in the desired format or quality.
 * 2. Event Modification: Provides capabilities to modify or augment events, which could include adding metadata, enriching with additional context, or performing other modifications as needed.
 * 3. Inherits all features of the TelemetryEventManager, such as event creation, persistent storage, and server transmission.
 */
export class VideoPlaybackTelemetry extends TelemetryEventManager {
  #playbackStatus = EVENT_PLAYBACK_STATUS.PAUSED
  #rangeStartedPosition = null as null | number
  #rangeEndedEventId: string | null = null
  #chapterEndedEventId: string | null = null

  #ranges!: TimeRanges
  #currentTimePosition = 0
  #maxTime = 0
  #showDuration = 0
  #currentChapter?: ChapterData
  #isComplete = false
  #progressTimerId = NaN

  public get ranges() {
    return this.#ranges
  }

  public async playbackStarted() {
    this.#playbackStatus = EVENT_PLAYBACK_STATUS.PLAYING
  }

  public async playbackPaused() {
    this.#playbackStatus = EVENT_PLAYBACK_STATUS.PAUSED
  }

  public async playbackEnded(reason: EVENT_PLAYBACK_STATUS) {
    this.#isComplete = reason == EVENT_PLAYBACK_STATUS.COMPLETED ? true : false
    clearTimeout(this.#progressTimerId)

    this.emitRangeEnded(this.#isComplete ? EVENT_PLAYBACK_STATUS.PENDING : EVENT_PLAYBACK_STATUS.SKIPPED, this.#rangeStartedPosition, this.currentTimePostion)
    this.emitEnded(reason, this.currentTimePostion)
    this.emitSendStorage()
  }

  public async playbackSeek(time: number, destinationTime: number) {
    this.#currentTimePosition = destinationTime

    this.emitRangeEnded(EVENT_PLAYBACK_STATUS.SKIPPED, this.#rangeStartedPosition, time)
    this.emitRangeStarted(destinationTime)
  }

  public async playbackTime(time: number, ranges: TimeRanges, duration: number) {
    this.#currentTimePosition = time
    this.#maxTime = Math.max(this.#maxTime, time)
    this.#showDuration = Math.abs(duration)
    this.#ranges = ranges
  }

  public async playbackChapterStarted(chapter?: ChapterProgressData) {
    if (!chapter) {
      this.log(this.name, '[playback:chapter:started:info] Chapter is null')
      return
    }

    this.emitChapterStarted(chapter)
  }

  public async playbackChapterEnded(previousChapter: ChapterProgressData, chapter: ChapterProgressData, reason: EVENT_PLAYBACK_STATUS) {
    this.emitChapterEnded(previousChapter, chapter, reason)
  }

  get isComplete(): boolean {
    return this.#isComplete
  }

  get showDuration(): number
  {
    return Number.isFinite(this.#showDuration) ? this.#showDuration : 0
  }

  get playbackDuration(): number {
    let totalDuration = 0
    if (!this.ranges || !this.ranges.length) return totalDuration

    for (let i = 0; i < this.ranges.length; i++) {
      totalDuration += this.ranges.end(i) - this.ranges.start(i)
    }

    return toFixed(totalDuration)
  }

  get playbackProgress(): number {
    return toFixed(this.getProgress(this.playbackDuration, this.#showDuration))
  }

  get currentTimePostion(): number {
    return this.#currentTimePosition
  }

  // ########## Emit Events #########

  public emitInitiated() {
    if (!this.session) {
      this.log(this.name, '[playback:emit:initiated:error]: Video telemetry session not initialized')
      return
    }

    this.#isComplete = false
    this.#maxTime = 0

    this.emitStarted()
    this.emitRangeStarted(0)

    clearTimeout(this.#progressTimerId)
    this.#progressTimerId = setInterval(() => {
      this.emitRangeEnded(EVENT_PLAYBACK_STATUS.PENDING, this.#rangeStartedPosition, this.currentTimePostion)
      this.emitProgressed()
      this.emitSendStorage(false)
    }, TIMER) as unknown as number
  }

  // This event sends all accumulated events from the storage
  public emitSendStorage(complete = true) {
    this.sendStored(complete).catch(error => {
      if (error?.name == MissingTelemetryEventsError.name) {
        return
      }
    })
  }

  // This event confirms the fact that user start watching the video.
  // Triggered only once when user starts watching the video.
  // It should not be triggered when user stopped and resumed playing the video during the watching.
  private emitStarted() {
    this.#chapterEndedEventId = null
    this.#rangeEndedEventId = null

    this.started()
  }

  // This event confirms the fact that user starts watching some range of the video (seeked some timeline mark).
  // It should not be triggered when resumed playing stopped video.
  private emitRangeStarted(time: number) {
    if (time == null) return

    this.#rangeStartedPosition = time

    this.rangeStarted(toFixed(time))
    this.emitRangeEnded(EVENT_PLAYBACK_STATUS.PENDING, time, time)
  }

  // This event confirms the fact that user finishes playing some range of the video or seeked some timeline mark.
  // Also triggers to update data with Pending status, for case when user close the page / internet connection issue etc...
  private emitRangeEnded(reason: EVENT_PLAYBACK_STATUS, startTime: number | null, endTime: number) {
    if (startTime === null) {
      this.log(this.name, '[playback:emit:range:ended:update:error] Range Started Position is missing', reason)
      return
    }

    const startTimestamp = toFixed(startTime)
    const endTimestamp = toFixed(endTime)
    const duration = toFixed(Math.abs(endTime - startTime))

    if (this.#rangeEndedEventId) {
      const record = this.store.find(record => record.id === this.#rangeEndedEventId)

      if (record) {
        record.event.data = {
          ...record.event.data,
          startTimestamp: Math.abs(startTimestamp),
          endTimestamp: Math.abs(endTimestamp),
          duration,
          reason
        }

        this.update(record.event)
      }

      if (reason !== EVENT_PLAYBACK_STATUS.PENDING) {
        this.#rangeEndedEventId = null
        this.#rangeStartedPosition = null
      }
      return
    }

    this.rangeEnded(startTimestamp, endTimestamp, duration, reason)
      .then(event => this.#rangeEndedEventId = event.id)
  }

  private emitChapterStarted(chapter: ChapterProgressData) {
    this.chapterStarted({
      id: chapter.id,
      marker: toFixed(chapter.marker),
      title: chapter.title || ''
    })

    this.emitChapterEnded(chapter, chapter, EVENT_PLAYBACK_STATUS.PENDING)
  }

  private emitChapterEnded(previousChapter: ChapterProgressData, chapter: ChapterProgressData, reason: EVENT_PLAYBACK_STATUS) {
    if (this.#chapterEndedEventId) {
      const record = this.store.find(record => record.id === this.#chapterEndedEventId) as StoreRecord<VideoChapterEndedTelemetryEvent>

      if (record) {
        const chapter = formatChapter(previousChapter, ['title', 'id'])
        delete chapter.id

        record.event.data = {
          ...record.event.data,
          chapter,
          reason,
        }

        this.update(record.event)

        if (reason !== EVENT_PLAYBACK_STATUS.PENDING) {
          this.#chapterEndedEventId = null
          return
        }
      }
    }

    if (!this.#chapterEndedEventId) {
      this.chapterEnded(
        toFixed(chapter.currentTimestamp),
        toFixed(chapter.maxTimestamp),
        toFixed(chapter.duration),
        toFixed(chapter.progress),
        {
          title: chapter.title || '',
          marker: toFixed(chapter.marker),
        },
        EVENT_PLAYBACK_STATUS.PENDING)
        .then(event => this.#chapterEndedEventId = event.id)
    }
  }

  private emitProgressed() {
    const chapter = this.#currentChapter ? {
      id: this.#currentChapter.id,
      title: this.#currentChapter.title,
      marker: this.#currentChapter.marker
    } as Chapter : undefined

    this.progressed(
      toFixed(Math.max(this.#currentTimePosition, 0)),
      toFixed(this.#maxTime),
      Math.abs(this.playbackProgress),
      Math.abs(this.playbackDuration),
      this.#playbackStatus,
      chapter)
  }

  // This event confirms the fact that user ends watching the video the video ended.
  // It’s not a technical event like “stop / pause”
  private emitEnded(reason = EVENT_PLAYBACK_STATUS.COMPLETED, time: number) {
    if (this.playbackDuration == 0) {
      this.log(this.name, '[playback:emit:ended:info]: Playback duration is zero')
      return
    }

    this.ended(
      toFixed(time),
      toFixed(this.#maxTime),
      toFixed(Math.abs(this.playbackProgress)),
      toFixed(Math.abs(this.playbackDuration)),
      reason,
      this.#currentChapter as Chapter
    )
  }


  private getProgress(duration: number, totalDuration: number): number {
    if (totalDuration === 0) {
      return 0
    }
    return (duration * 100) / totalDuration
  }
}

// Helper methods
function formatChapter<T>(chapter: T, skip: Array<keyof T> = []) {
  return Object.entries(chapter as never).reduce((acc, [key, value]) => {
    // @ts-expect-error: ignore
    if (!skip.includes(key)) acc[key] = toFixed(value)
    // @ts-expect-error: ignore
    else acc[key] = value

    return acc
  }, {} as T)
}
